<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { ServerError } from "@/api/server";
import { login } from "@/stores/auth";

const name = ref("");
const password = ref("");
const busy = ref(false);
const error = ref("");
const nameField = ref<HTMLInputElement | null>(null);

onMounted(() => nextTick(() => nameField.value?.focus()));

async function submit(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    await login(name.value.trim(), password.value);
  } catch (cause) {
    error.value =
      cause instanceof ServerError && cause.status === 429
        ? "Zu viele Versuche. Bitte ein paar Minuten warten."
        : cause instanceof ServerError && cause.status === 401
          ? "Benutzername oder Passwort stimmt nicht."
          : cause instanceof ServerError
            ? cause.message
            : String(cause);
    password.value = "";
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <main class="wrap">
    <form class="panel box" @submit.prevent="submit">
      <h1>KinderService</h1>
      <p class="hint">Bitte anmelden, um das Dashboard zu sehen.</p>

      <label>
        Benutzer
        <input ref="nameField" v-model="name" type="text" autocomplete="username" required />
      </label>

      <label>
        Passwort
        <input v-model="password" type="password" autocomplete="current-password" required />
      </label>

      <p v-if="error" class="error">{{ error }}</p>

      <button type="submit" :disabled="busy || !name.trim() || !password">
        {{ busy ? "Anmelden …" : "Anmelden" }}
      </button>
    </form>
  </main>
</template>

<style scoped>
.wrap {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 20px;
}

.box {
  padding: 24px;
  width: min(360px, 100%);
  display: grid;
  gap: 14px;
}

h1 {
  font-size: 20px;
  margin: 0;
}

.hint {
  color: var(--muted);
  font-size: 13.5px;
  margin: -6px 0 4px;
}

label {
  display: grid;
  gap: 4px;
  font-size: 13px;
  color: var(--muted);
}

label input {
  width: 100%;
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: 13.5px;
}

button {
  margin-top: 4px;
  padding: 9px 12px;
  background: color-mix(in srgb, var(--accent) 30%, var(--panel2));
  border-color: var(--accent);
}
</style>
